import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const SHOW_AFTER_VH = 1.5;      // reveal once scrolled past 1.5 screens
const VERY_LONG_VH = 6;         // "jump to bottom" only on pages taller than 6 screens
const NEAR_BOTTOM_VH = 1.5;     // ...and only while more than 1.5 screens remain below

// Sits bottom-LEFT of the content area (the bottom-right corner belongs to the
// AI Copilot bubble, proactive prompts and voice guidance). On mobile it hides
// while scrolling down and re-appears on scroll-up, clears the bottom nav and
// the iPhone safe-area, and disappears whenever a dialog locks the page scroll.
export const BackToTop = ({ desktopLeftClass = 'lg:left-[calc(16rem+1.5rem)]' }) => {
  const [visible, setVisible] = useState(false);
  const [showBottom, setShowBottom] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const lastY = useRef(0);
  const upwardSince = useRef(false);

  useEffect(() => {
    let raf = 0;
    const evaluate = () => {
      raf = 0;
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const vh = window.innerHeight || 1;
      const docH = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const goingUp = y < lastY.current - 2;
      const goingDown = y > lastY.current + 2;
      if (goingUp) upwardSince.current = true;
      if (goingDown) upwardSince.current = false;
      lastY.current = y;

      const pastThreshold = y > vh * SHOW_AFTER_VH;
      setVisible(pastThreshold && (isDesktop || upwardSince.current));
      const remaining = docH - (y + vh);
      setShowBottom(docH > vh * VERY_LONG_VH && remaining > vh * NEAR_BOTTOM_VH);
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(evaluate); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // Radix dialogs/sheets stamp data-scroll-locked on <body>; hide while any is open.
  useEffect(() => {
    const sync = () => setDialogOpen(document.body.hasAttribute('data-scroll-locked'));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked', 'style'] });
    return () => mo.disconnect();
  }, []);

  const scrollTo = (top) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
  };

  const shown = visible && !dialogOpen;
  const base = 'pointer-events-auto w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-slate-200 text-[#283F8B] shadow-md ' +
    'flex items-center justify-center transition-[opacity,transform,background-color] duration-200 ' +
    'hover:bg-[#283F8B] hover:text-white hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#283F8B]/50 active:scale-95';

  return (
    <div
      aria-hidden={!shown}
      data-testid="back-to-top-wrap"
      className={`print:hidden fixed z-40 left-4 ${desktopLeftClass} bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-6 flex flex-col gap-2 ` +
        `transition-[opacity,transform] duration-300 ease-out ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'}`}
    >
      {showBottom && (
        <button
          type="button"
          aria-label="Jump to bottom of page"
          title="Jump to bottom"
          data-testid="jump-to-bottom-btn"
          tabIndex={shown ? 0 : -1}
          onClick={() => scrollTo(document.documentElement.scrollHeight)}
          className={`${base} w-8 h-8 self-center opacity-80 hover:opacity-100`}
        >
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        type="button"
        aria-label="Back to top"
        title="Back to top"
        data-testid="back-to-top-btn"
        tabIndex={shown ? 0 : -1}
        onClick={() => scrollTo(0)}
        className={base}
      >
        <ArrowUp className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BackToTop;
